import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { phoneLayoutQuery } from './device';

// Touch gets the depth and reflected light that a pointer supplies on desktop.
// One observer and one scheduled frame serve the visible artwork across every route.
export default function TouchAtmosphere() {
  const { pathname } = useLocation();
  useEffect(() => {
    const root = document.documentElement;
    const small = matchMedia(phoneLayoutQuery);
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const selector = '.observatory-scene,.oracle-card,.journal-cover,.article-cover-page,.founder-letter-photo,.founder-portrait,.booking-seal,.certificate-grid > button';
    const nodes = new Set<HTMLElement>();
    const visible = new Set<HTMLElement>();
    let frame = 0;
    let touchActive = false;
    const allowed = () => small.matches && !reduced.matches && root.dataset.motion !== 'paused';
    const update = () => {
      frame = 0;
      const enabled = allowed();
      root.dataset.touchEffects = enabled ? 'on' : 'off';
      if (!enabled) {
        if (touchActive) nodes.forEach(node => { node.style.removeProperty('--orbit-x'); node.style.removeProperty('--orbit-y'); });
        touchActive = false;
        return;
      }
      touchActive = true;
      const measurements = Array.from(visible).map(node => ({ node, box: node.getBoundingClientRect() }));
      for (const { node, box } of measurements) {
        const vertical = Math.max(-1, Math.min(1, (box.top + box.height / 2 - window.innerHeight / 2) / (window.innerHeight / 2)));
        const horizontal = Math.max(-1, Math.min(1, (box.left + box.width / 2 - window.innerWidth / 2) / (window.innerWidth / 2)));
        const presence = 1 - Math.min(1, Math.abs(vertical) * .65 + Math.abs(horizontal) * .65);
        node.style.setProperty('--touch-depth', vertical.toFixed(3));
        node.style.setProperty('--touch-presence', presence.toFixed(3));
        node.style.setProperty('--touch-lean', `${(horizontal * -3 + vertical * 2).toFixed(2)}deg`);
        node.style.setProperty('--touch-light', `${((1 - vertical) * 50).toFixed(1)}%`);
        if (node.classList.contains('observatory-scene')) {
          node.style.setProperty('--orbit-x', `${horizontal * 5}deg`);
          node.style.setProperty('--orbit-y', `${vertical * -9}deg`);
        }
      }
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    const observer = new IntersectionObserver(entries => {
      entries.forEach(({ target, isIntersecting }) => {
        if (isIntersecting) visible.add(target as HTMLElement); else visible.delete(target as HTMLElement);
      });
      schedule();
    });
    const discover = () => {
      nodes.forEach(node => { if (!node.isConnected) { observer.unobserve(node); nodes.delete(node); visible.delete(node); } });
      document.querySelectorAll<HTMLElement>(selector).forEach(node => {
        if (!nodes.has(node)) { nodes.add(node); observer.observe(node); }
      });
      schedule();
    };
    const mutations = new MutationObserver(discover);
    mutations.observe(document.getElementById('root')!, { childList: true, subtree: true });
    document.addEventListener('scroll', schedule, { capture: true, passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('radian-motion-change', schedule);
    small.addEventListener('change', schedule);
    reduced.addEventListener('change', schedule);
    discover();
    return () => {
      cancelAnimationFrame(frame); observer.disconnect(); mutations.disconnect();
      document.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule); window.removeEventListener('radian-motion-change', schedule);
      small.removeEventListener('change', schedule); reduced.removeEventListener('change', schedule);
      if (touchActive) nodes.forEach(node => { node.style.removeProperty('--orbit-x'); node.style.removeProperty('--orbit-y'); });
      delete root.dataset.touchEffects;
    };
  }, [pathname]);
  return null;
}
